import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { fdatetime } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";

const ACTION_STYLES = {
  create: "border-emerald-500/40 text-emerald-500",
  update: "border-sky-500/40 text-sky-500",
  delete: "border-red-500/40 text-red-500",
  open: "border-emerald-500/40 text-emerald-500",
  close: "border-amber-500/40 text-amber-500",
  login: "border-muted-foreground/40 text-muted-foreground",
};
const ACTION_LABELS = { create: "Criou", update: "Editou", delete: "Excluiu", open: "Abriu", close: "Fechou", login: "Login" };
const ENTITY_LABELS = {
  sales: "Venda", students: "Aluno", products: "Produto", plans: "Plano",
  payment_methods: "Pagamento", movements: "Movimentação", cash: "Caixa",
  users: "Usuário", settings: "Configurações", auth: "Acesso",
};

export default function Auditoria() {
  const { data, isLoading } = useQuery({
    queryKey: ["audit"],
    queryFn: async () => (await api.get("/audit")).data,
  });

  return (
    <div className="space-y-6" data-testid="auditoria-page">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Auditoria</h1>
        <p className="text-sm text-muted-foreground">
          Registro completo de ações — nada é apagado definitivamente (exclusão lógica)
        </p>
      </div>

      <Card>
        <CardContent className="overflow-x-auto p-0">
          {isLoading ? (
            <div className="p-6 space-y-2">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-10" />)}</div>
          ) : (
            <Table data-testid="audit-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Data/Hora</TableHead>
                  <TableHead>Usuário</TableHead>
                  <TableHead>Ação</TableHead>
                  <TableHead>Entidade</TableHead>
                  <TableHead>Detalhes</TableHead>
                  <TableHead>IP</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data || []).map((a) => (
                  <TableRow key={a.id} data-testid={`audit-row-${a.id}`}>
                    <TableCell className="whitespace-nowrap">{fdatetime(a.created_at)}</TableCell>
                    <TableCell className="font-medium">{a.user_name}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={ACTION_STYLES[a.action] || ""}>
                        {ACTION_LABELS[a.action] || a.action}
                      </Badge>
                    </TableCell>
                    <TableCell>{ENTITY_LABELS[a.entity] || a.entity}</TableCell>
                    <TableCell className="max-w-80">
                      {(a.before || a.after) && (
                        <details className="text-xs text-muted-foreground">
                          <summary className="cursor-pointer hover:text-foreground" data-testid={`audit-detail-${a.id}`}>
                            ver alterações
                          </summary>
                          <pre className="mt-2 max-h-40 overflow-auto rounded-lg bg-muted/60 p-2 text-[10px]">
                            {JSON.stringify({ antes: a.before, depois: a.after }, null, 1)}
                          </pre>
                        </details>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{a.ip || "—"}</TableCell>
                  </TableRow>
                ))}
                {(data || []).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="py-10 text-center text-sm text-muted-foreground">
                      Nenhum registro de auditoria ainda.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
